import axios from 'axios';

// Construct API base URL using the base path environment variable
const getApiBaseUrl = () => {
  const basePath = process.env.REACT_APP_BASE_PATH || "/";
  // Ensure proper path joining - add / between basePath and api
  const apiPath = basePath === "/" ? "/api" : `${basePath}/api`;
  
  // In development, always use localhost backend
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000';
  }
  
  // In production, use the constructed API path
  return apiPath;
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
});

// Attach token from localStorage to every request (except iframe routes)
api.interceptors.request.use((config) => {
  try {
    // Set withCredentials only for iframe endpoints
    const isIframeEndpoint = config.url && config.url.includes('/iframe/');
    config.withCredentials = !!isIframeEndpoint;
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      // Backend expects the raw token (no "Bearer " prefix)
      config.headers.Authorization = token;
    }
  } catch (_) {
    // no-op if localStorage unavailable
  }
  return config;
});

// Google Analytics API endpoints

export const fetchVisitorsByCountryGA = async (startDate, endDate) => {
  const response = await api.get('/ga/visitors-by-country', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data; 
};

export const fetchVisitorsByPageGA = async (startDate, endDate) => {
  const response = await api.get('/ga/visitors-by-page', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data; 
};

// Deprecated: Use fetchVisitorsByCountryGA or fetchVisitorsByPageGA instead
export const fetchVisitorsGA = async (startDate, endDate) => {
  const response = await api.get('/ga/visitors-by-page', {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data; 
};

// Placeholder: will call backend when implemented
export const fetchCompetitions = async () => {
  const response = await api.get('/competitions');
  return response.data;
};

export const fetchCompetitionById = async (competitionId) => {
  const response = await api.get('/competition_by_id', {
    params: { competitionId }
  });
  return response.data;
};

// month: "YYYY-MM"
export const fetchMonthlyReport = async (month) => {
  const response = await api.get('/monthly_report', {
    params: { month }
  });
  return response.data;
};

export const fetchActiveUsersByWeek = async () => {
  const response = await api.get('/active_users_by_week');
  return response.data; 
};

export const fetchActiveUsersByDayAndWeek = async () => {
  const response = await api.get('/active_users_by_day_and_week');
  return response.data; 
};

export const fetchAverageUserActivity = async () => {
  const response = await api.get('/average_user_activity');
  return response.data; 
};

export const fetchFasterUsersByMonth = async () => {
  const response = await api.get('/faster_users_by_month');
  return response.data; 
};

export const fetchVitalStats = async () => {
  const response = await api.get('/vital_stats');
  return response.data; 
};

export const fetchActivityStats = async () => {
  const response = await api.get('/activity_stats');
  return response.data; 
};

export const fetchCompetition_5_2025 = async () => {
  const response = await api.get('/competition_5_2025');
  return response.data; 
};

export const fetchCountryCount = async () => {
  const response = await api.get('/country_count');
  return response.data; 
};

export const fetchPieces = async (startDate, promptId) => {
  const response = await api.get('/pieces_query', {
    params: { start_date: startDate, prompt_id: promptId }
  });
  return response.data; 
};

export const fetchCountPublishedReviewsForPrompt = async (promptId) => {
  const response = await api.get('/count_published_reviews_for_prompt', {
    params: { prompt_id: promptId }
  });
  return response.data; 
};

export const fetchPieceReferrals = async (startDate, promptId) => {
  const response = await api.get('/piece_referrals', {
    params: { start_date: startDate, prompt_id: promptId }
  });
  return response.data; 
};

export const fetchProfilesCountByCountryMonth = async () => {
  const response = await api.get('/profiles_count_by_country_month');
  return response.data; 
};

export const fetchProfilesReferrals = async () => {
  const response = await api.get('/profiles_referrals');
  return response.data; 
};

export const fetchEducatorDomains = async () => {
  const response = await api.get('/educator_domains');
  return response.data; 
};

export const fetchUsDemographics = async () => {
  const response = await api.get('/us_demographics');
  return response.data; 
};

export const fetchRaceSurveyUserCounts = async () => {
  const response = await api.get('/race_survey_user_counts');
  return response.data; 
};

// Clara API endpoints (Authenticated - requires login)
export const fetchInviteClaraResponseCategorization = async (assignmentCodeId) => {
  const response = await api.get('/invite-clara-response-categorization', {
    params: { assignment_code_id: assignmentCodeId }
  });
  return response.data;
};

export const fetchWtwClaraResponseCategorization = async (promptId) => {
  const response = await api.get('/wtw-clara-response-categorization', {
    params: { prompt_id: promptId }
  });
  return response.data;
};

// Clara Iframe Endpoints (Origin-validated, no login required)
/**
 * @param {number|null} id - Assignment code ID or prompt ID (optional - omit for all IDs)
 * @param {string} source - Data source: "invite-clara" or "wtw-clara"
 * @param {number} envType - Environment type: 0 (test) or 1 (production) or 2 (dev)
 * @param {string} categoryType - Category type: "clara_response_category" or "user_query_category"
 * @returns {Promise<{success: boolean, data: Array, environment: string, category_type: string}>}
 */
export const fetchClaraResponseCategorizationForIframe = async (id, source, envType, categoryType) => {
  const params = {
    source,
    env_type: envType,
    category_type: categoryType
  };
  
  // Only include id if provided (not null/empty)
  if (id) {
    params.id = id;
  }
  
  const response = await api.get('/iframe/clara-response-categorization', { params });
  return response.data;
};

/**
 * @param {number|null} id - Assignment code ID or prompt ID (optional - omit for all IDs)
 * @param {string} source - Data source: "invite-clara" or "wtw-clara"
 * @param {number} envType - Environment type: 0 (test) or 1 (production) or 2 (dev)
 * @param {string} categoryType - Category type: "clara_response_category" or "user_query_category"
 * @returns {Promise<{success: boolean, data: Array, environment: string, category_type: string}>}
 */
export const fetchClaraResponseCategorizationByDateForIframe = async (id, source, envType, categoryType) => {
  const params = {
    source,
    env_type: envType,
    category_type: categoryType
  };
  
  // Only include id if provided (not null/empty)
  if (id) {
    params.id = id;
  }
  
  const response = await api.get('/iframe/clara-response-categorization-by-date', { params });
  return response.data;
};

// Clara API endpoints 
export const fetchClaraAiUsage = async () => {
  const response = await api.get('/clara_ai_usage');
  return response.data;
};

export const fetchClaraUsagePercentage = async () => {
  const response = await api.get('/clara_usage_percentage');
  return response.data;
};

export const fetchClaraUsageOverTime = async () => {
  const response = await api.get('/clara_usage_over_time');
  return response.data;
};

export const fetchAverageClaraRating = async () => {
  const response = await api.get('/average_clara_rating');
  return response.data;
};